import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiSelect,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OplBoard, OplItem, OplItemType } from "src/questionnaires";
import { AnketaResourcesEnum } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useOpsanioCan } from "../can";
import { useToast } from "src/notifications";
import { OplBoardView, allPeople, fmtDate } from "./format";
import { OplItemInput, useOplApi } from "./api";
import { MeetingHead, OplSendState } from "./meeting-head";
import { PointList } from "./point-list";
import { PointDetail } from "./point-detail";
import {
  AbsenceWarning,
  MeetingDialog,
  NewPointDialog,
  SplitDialog,
} from "./dialogs";
import "./opl-theme.css";

const i18n = "anketa.opsanio.opl";

/**
 * The OPL board: one appliance's Regeltermine and the points raised in them.
 *
 * Full page rather than a flyout tab because the working layout is three
 * columns - the round's head, the points in working order, and the selected
 * point with its log - and that needs roughly 1300px. The flyout keeps a
 * summary that links here.
 */
export const OpsanioOplPage = (): JSX.Element => {
  const { appliance = "" } = useParams<{ appliance: string }>();
  const { translate } = useAnketaI18n();
  // Board writes are opsanio/opl · create|update, route-narrowed to internal
  // orgs; promoting to OTOBO also needs opsanio/otobo · create.
  const can = useOpsanioCan();
  const canCreate = can(AnketaResourcesEnum.opsanioOpl, "create", { isInternal: true });
  const canUpdate = can(AnketaResourcesEnum.opsanioOpl, "update", { isInternal: true });
  const canPromoteOtobo =
    canCreate && can(AnketaResourcesEnum.opsanioOtobo, "create", { isInternal: true });
  const { addToast } = useToast();
  const t = useCallback(
    (k: string, d: string, values?: Record<string, unknown>) =>
      translate(`${i18n}.${k}`, { defaultMessage: d, values }),
    [translate],
  );

  const api = useOplApi(appliance);
  const navigate = useNavigate();
  // `?item=<id>` (from the cross-appliance overview) opens that point.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedItem = searchParams.get("item");

  const [board, setBoard] = useState<OplBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const [roundId, setRoundId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [view, setView] = useState<OplBoardView>("meeting");
  const [typeFilter, setTypeFilter] = useState<OplItemType | "all">("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [logError, setLogError] = useState<string | null>(null);
  const [sendState, setSendState] = useState<OplSendState>("idle");

  const [newOpen, setNewOpen] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);
  const [splitFor, setSplitFor] = useState<OplItem | null>(null);
  const [headOpen, setHeadOpen] = useState(false);
  const [absence, setAbsence] = useState<
    { message: string; retry: () => Promise<void> } | null
  >(null);

  const notifyFailure = useCallback(
    (title: string, error?: string) => addToast({ title, color: "danger", text: error }),
    [addToast],
  );

  const load = useCallback(
    async (keepSelection = true) => {
      if (!api.canCall) return;
      setLoading(true);
      setLoadError(null);
      setForbidden(false);

      const result = await api.board();
      if (result.status === 403) {
        setForbidden(true);
        setLoading(false);
        return;
      }
      if (!result.ok || !result.data) {
        setLoadError(result.error ?? "Die OPL konnte nicht geladen werden.");
        setLoading(false);
        return;
      }

      setBoard(result.data);
      setRoundId((prev) =>
        prev && result.data!.meetings.some((m) => m.id === prev)
          ? prev
          : result.data!.currentMeetingId,
      );
      if (!keepSelection) setSelectedId(null);
      setLoading(false);
    },
    [api],
  );

  useEffect(() => {
    load(false);
    // Only when the appliance changes; `load` is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliance, api.canCall]);

  // Memoised: `?? []` would hand a fresh array to every dependent memo on each
  // render, defeating them.
  const meetings = useMemo(() => board?.meetings ?? [], [board]);
  const items = useMemo(() => board?.items ?? [], [board]);
  const round = useMemo(
    () => meetings.find((m) => m.id === roundId) ?? null,
    [meetings, roundId],
  );
  // Deep link: select the requested point once the board has loaded, then
  // drop the parameter so closing the point does not reopen it.
  useEffect(() => {
    if (!requestedItem || !board) return;
    if (items.some((i) => i.id === requestedItem)) setSelectedId(requestedItem);
    searchParams.delete("item");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedItem, board, items]);

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  // The button reflects what the round records, not what this session did.
  useEffect(() => {
    setSendState(round?.summarySentAt ? "sent" : "idle");
  }, [round?.id, round?.summarySentAt]);

  /**
   * Seeds the very first round. Deliberately only used when there is no round
   * yet: once a team has curated who attends, re-deriving from the
   * questionnaire would throw that away.
   */
  const defaultParticipants = useMemo(
    () =>
      (board?.defaultParticipants ?? []).map((p) => ({
        name: p.name,
        email: p.email ?? "",
        source: p.source,
        present: p.present ?? true,
        isGuest: p.isGuest ?? false,
      })),
    [board],
  );

  /**
   * Owner candidates: everyone who has ever been in a round for this appliance,
   * flagged by whether they are in the one on screen. Assigning outside the room
   * is allowed - the round just has to do it deliberately.
   */
  const people = useMemo(() => allPeople(meetings, round), [meetings, round]);

  /** The round after the one on screen, for the "Nächster Termin" line. */
  const nextDate = useMemo(() => {
    if (!round) return null;
    const later = meetings.find((m) => m.sequence === round.sequence + 1);
    return later?.date ?? null;
  }, [meetings, round]);

  // --- actions ------------------------------------------------------------

  /**
   * Run a write, then refresh. On `409 ABSENT_OWNER` the call is parked behind
   * the warning dialog instead of failing: the operator decides, then it reruns
   * with the acknowledgement.
   */
  const run = useCallback(
    async (
      action: () => Promise<{ ok: boolean; error?: string; absentOwner?: boolean }>,
      failureTitle: string,
      retryAcknowledged?: () => Promise<{ ok: boolean; error?: string } | void>,
    ) => {
      setBusy(true);
      const result = await action();
      setBusy(false);

      if (result.absentOwner && retryAcknowledged) {
        setAbsence({
          message: result.error ?? "",
          retry: async () => {
            setAbsence(null);
            setBusy(true);
            const retried = await retryAcknowledged();
            setBusy(false);
            // The acknowledged rerun can still be denied or fail - say so
            // instead of silently reloading.
            if (retried && !retried.ok) notifyFailure(failureTitle, retried.error);
            await load();
          },
        });
        return false;
      }
      if (!result.ok) {
        notifyFailure(failureTitle, result.error);
        return false;
      }
      await load();
      return true;
    },
    [load, notifyFailure],
  );

  const patchItem = useCallback(
    async (patch: { dueDate?: string; owner?: string }) => {
      if (!selected || !round) return;
      await run(
        () => api.updateItem(selected.id, { ...patch, meetingId: round.id }),
        t("error.title", "OPL nicht verfügbar"),
        async () => {
          return api.updateItem(selected.id, {
            ...patch,
            meetingId: round.id,
            acknowledgeAbsence: true,
          });
        },
      );
    },
    [api, selected, round, run, t],
  );

  const appendLog = useCallback(
    async (text: string) => {
      if (!selected || !round) return;
      if (!text.trim()) {
        setLogError(
          t(
            "detail.logEmpty",
            "Leerer Eintrag. Bei jedem behandelten Punkt wird ein Eintrag ergänzt - notfalls „keine Änderung“.",
          ),
        );
        return;
      }
      setLogError(null);
      await run(
        () => api.appendLog(selected.id, text, round.id),
        t("error.title", "OPL nicht verfügbar"),
      );
    },
    [api, selected, round, run, t],
  );

  const discard = useCallback(
    async (reason: string) => {
      if (!selected || !round) return;
      if (!reason.trim()) {
        setLogError(
          t(
            "detail.discardNeedsReason",
            "Verwerfen verlangt eine Begründung im Log. Text eintragen, dann verwerfen.",
          ),
        );
        return;
      }
      setLogError(null);
      await run(
        () => api.closeItem(selected.id, "discarded", reason, round.id),
        t("error.title", "OPL nicht verfügbar"),
      );
    },
    [api, selected, round, run, t],
  );

  /**
   * Open a round starting now, outside the weekly rhythm.
   *
   * One click, no form: the point of a spontaneous round is that it is already
   * happening. It gets today's date, the current round's recorder, and - like
   * every round - the appliance's default participants, seeded server-side.
   *
   * Deliberately does NOT mark the scheduled round held: that one has not taken
   * place yet, and an ad-hoc session in between says nothing about it. Its key
   * is suffixed automatically when the ISO week already has a round, so
   * `2026-KW33-2` reads as "the second session that week".
   */
  const startAdHocRound = useCallback(async () => {
    setBusy(true);
    const result = await api.createMeeting({
      date: new Date().toISOString(),
      title: t("adhoc.defaultTitle", "Spontantermin"),
      status: "planned",
      recorder: round?.recorder ?? undefined,
      durationMinutes: round?.durationMinutes ?? undefined,
    });
    setBusy(false);

    if (!result.ok || !result.data) {
      notifyFailure(
        t("adhoc.failed", "Spontantermin konnte nicht angelegt werden"),
        result.error,
      );
      return;
    }
    // Jump straight into it - you are in the meeting.
    setRoundId(result.data.id);
    setSelectedId(null);
    addToast({
      title: t("adhoc.created", "Spontantermin angelegt"),
      color: "success",
      text: result.data.key,
    });
    await load();
  }, [api, round, addToast, notifyFailure, load, t]);

  /**
   * Mail the minutes to the round's participants.
   *
   * A refused send is a warning, not an error: the backend has already marked
   * the round held and answers 200 with `sent: false`, so the operator is told
   * and the button stays available for a retry.
   */
  const sendSummary = useCallback(async () => {
    if (!round) return;
    setSendState("sending");
    const result = await api.sendSummary(round.id, {});

    if (!result.ok) {
      setSendState("failed");
      notifyFailure(
        t("summary.failedTitle", "Protokoll konnte nicht versendet werden"),
        result.error,
      );
      return;
    }
    if (result.data?.sent === false) {
      setSendState("failed");
      addToast({
        title: t("summary.failedTitle", "Protokoll konnte nicht versendet werden"),
        color: "warning",
        text: result.data.warning,
      });
    } else {
      setSendState("sent");
      addToast({
        title: t("summary.sentToast", "Protokoll versendet"),
        color: "success",
        text: `${result.data?.recipients.length ?? 0}`,
      });
    }
    await load();
  }, [api, round, addToast, notifyFailure, load, t]);

  const promote = useCallback(
    async (target: "gitlab" | "otobo") => {
      if (!selected) return;
      setBusy(true);
      const result =
        target === "gitlab"
          ? await api.promoteToGitlab(selected.id)
          : await api.promoteToOtobo(selected.id);
      setBusy(false);

      if (result.unavailable || result.conflict) {
        addToast({ title: result.error ?? "", color: "warning" });
        return;
      }
      if (!result.ok) {
        notifyFailure(t("error.title", "OPL nicht verfügbar"), result.error);
        return;
      }
      if (result.data?.persisted === false) {
        addToast({ title: result.data.warning ?? "", color: "warning" });
      }
      await load();
    },
    [api, selected, addToast, notifyFailure, load, t],
  );

  // --- render -------------------------------------------------------------

  if (!appliance) {
    return (
      <div className="oplRoot" style={{ padding: 24 }}>
        <EuiCallOut
          color="warning"
          iconType="help"
          title={t("noInstance.title", "Keine Appliance-Kennung")}
        >
          <p>{t("noInstance.body", "Ohne VPC-Namen kann keine OPL zugeordnet werden.")}</p>
        </EuiCallOut>
      </div>
    );
  }

  if (loading && !board) {
    return (
      <div className="oplRoot" style={{ padding: 48, textAlign: "center" }}>
        <EuiLoadingSpinner size="xl" />
      </div>
    );
  }

  if (forbidden) {
    return (
      <div className="oplRoot" style={{ padding: 24 }}>
        <EuiCallOut
          color="warning"
          iconType="lock"
          title={t("forbidden.title", "Kein Zugriff auf die OPL")}
        >
          <p>{t("forbidden.body", "Ihrer Rolle fehlt die Berechtigung `opsanio/opl`.")}</p>
        </EuiCallOut>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="oplRoot" style={{ padding: 24 }}>
        <EuiCallOut color="danger" iconType="alert" title={t("error.title", "OPL nicht verfügbar")}>
          <p>{loadError}</p>
          <EuiButton size="s" color="danger" iconType="refresh" onClick={() => load()}>
            {t("error.retry", "Erneut versuchen")}
          </EuiButton>
        </EuiCallOut>
      </div>
    );
  }

  const header = (
    <EuiFlexGroup
      alignItems="center"
      gutterSize="m"
      responsive={false}
      wrap
      style={{
        padding: "12px 20px",
        borderBottom: "1px solid var(--opl-line)",
        flexGrow: 0,
      }}
    >
      <EuiFlexItem grow={false}>
        <EuiTitle size="xs">
          <h1>{t("title", "OPL")}</h1>
        </EuiTitle>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiText size="xs" color="subdued">
          {t("subtitle", "Fortlaufende Offene-Punkte-Liste")} · {appliance}
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButtonEmpty
          size="xs"
          iconType="list"
          onClick={() => navigate(`/${AnketaResourcesEnum.opsanio}/opl`)}
        >
          {t("overview.link", "All open points")}
        </EuiButtonEmpty>
      </EuiFlexItem>
      <EuiFlexItem />
      <EuiFlexItem grow={false} style={{ minWidth: 230 }}>
        <EuiSelect
          compressed
          prepend={t("round", "Runde")}
          value={roundId ?? ""}
          options={meetings
            .slice()
            .reverse()
            .map((m) => ({
              value: m.id,
              text: `${m.key} · ${fmtDate(m.date)}${
                m.title ? ` · ${m.title}` : ""
              }${
                m.id === board?.currentMeetingId ? `  (${t("round.current", "aktuell")})` : ""
              }`,
            }))}
          onChange={(e) => {
            setRoundId(e.target.value);
            setSelectedId(null);
          }}
          disabled={meetings.length === 0}
          aria-label={t("round", "Runde")}
        />
      </EuiFlexItem>
      {canCreate && (
      <>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="sortRight"
          iconSide="right"
          onClick={() =>
            run(() => api.advanceRound(), t("error.title", "OPL nicht verfügbar"))
          }
          disabled={busy || meetings.length === 0}
        >
          {t("round.advance", "Runde weiterschalten")}
        </EuiButton>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="clock"
          onClick={startAdHocRound}
          disabled={busy}
          title={t(
            "adhoc.hint",
            "Runde ab jetzt, ausserhalb des wöchentlichen Rhythmus. Der geplante Termin bleibt unberührt.",
          )}
        >
          {t("adhoc.start", "Spontantermin")}
        </EuiButton>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          fill
          iconType="plusInCircle"
          onClick={() => {
            setNewError(null);
            setNewOpen(true);
          }}
          disabled={busy || !round}
        >
          {t("point.new", "Neuer Punkt")}
        </EuiButton>
      </EuiFlexItem>
      </>
      )}
      <EuiFlexItem grow={false}>
        <EuiButtonEmpty
          size="xs"
          iconType="refresh"
          onClick={() => load()}
          isLoading={loading}
        >
          {t("refresh", "Aktualisieren")}
        </EuiButtonEmpty>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  if (meetings.length === 0) {
    return (
      <div className="oplRoot oplPage">
        {header}
        <EuiEmptyPrompt
          iconType="calendar"
          title={<h2>{t("empty.title", "Noch keine Runde erfasst")}</h2>}
          body={<EuiText size="s" color="subdued"><p>{t("empty.body", "Die OPL beginnt mit der ersten Runde.")}</p></EuiText>}
          actions={
            canCreate ? (
              <EuiButton fill iconType="plusInCircle" onClick={() => setHeadOpen(true)}>
                {t("empty.create", "Erste Runde anlegen")}
              </EuiButton>
            ) : undefined
          }
        />
        {headOpen && (
          <MeetingDialog
            meeting={null}
            saving={busy}
            defaultParticipants={defaultParticipants}
            contactSuggestions={people.map((p) => ({ value: p.value, label: p.label }))}
            onCancel={() => setHeadOpen(false)}
            onSubmit={async (value) => {
              const ok = await run(
                () => api.createMeeting(value),
                t("error.title", "OPL nicht verfügbar"),
              );
              if (ok) setHeadOpen(false);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="oplRoot oplPage">
      {header}

      <div
        className="oplBoard"
        style={
          {
            "--opl-detail-width": selected ? "404px" : "0px",
          } as React.CSSProperties
        }
      >
        <MeetingHead
          meeting={round}
          nextDate={nextDate}
          sendState={sendState}
          recipientCount={
            (round?.attendees ?? []).filter((a) => (a.email ?? "").trim() !== "").length
          }
          onEdit={() => setHeadOpen(true)}
          onSendSummary={sendSummary}
          canUpdate={canUpdate}
        />

        <PointList
          items={items}
          meetings={meetings}
          round={round}
          view={view}
          onViewChange={setView}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          ownerFilter={ownerFilter}
          onOwnerFilterChange={setOwnerFilter}
          selectedId={selectedId}
          onSelect={(item) => {
            setSelectedId(item.id);
            setLogError(null);
          }}
        />

        {selected && (
          <PointDetail
            item={selected}
            round={round}
            people={people}
            busy={busy}
            logError={logError}
            onClose={() => setSelectedId(null)}
            onPatch={patchItem}
            onAppendLog={appendLog}
            onDone={() =>
              round &&
              run(
                () => api.closeItem(selected.id, "done", undefined, round.id),
                t("error.title", "OPL nicht verfügbar"),
              )
            }
            onDiscard={discard}
            onReopen={(reason) =>
              round &&
              run(
                () => api.reopenItem(selected.id, reason || "Wieder geöffnet.", round.id),
                t("error.title", "OPL nicht verfügbar"),
              )
            }
            onSplit={() => setSplitFor(selected)}
            onPromote={promote}
            canUpdate={canUpdate}
            canCreate={canCreate}
            canPromoteOtobo={canPromoteOtobo}
          />
        )}
      </div>

      {/* --- dialogs --- */}
      {newOpen && (
        <NewPointDialog
          round={round}
          people={people}
          saving={busy}
          error={newError}
          onCancel={() => setNewOpen(false)}
          onSubmit={async (value: OplItemInput) => {
            const ok = await run(
              () => api.createItem({ ...value, meetingId: round?.id }),
              t("error.title", "OPL nicht verfügbar"),
              async () => {
                const retried = await api.createItem({
                  ...value,
                  meetingId: round?.id,
                  acknowledgeAbsence: true,
                });
                if (retried.ok) setNewOpen(false);
                return retried;
              },
            );
            if (ok) setNewOpen(false);
          }}
        />
      )}

      {splitFor && (
        <SplitDialog
          item={splitFor}
          round={round}
          saving={busy}
          onCancel={() => setSplitFor(null)}
          onSubmit={async (titles) => {
            const ok = await run(
              () => api.splitItem(splitFor.id, titles, round?.id),
              t("error.title", "OPL nicht verfügbar"),
            );
            if (ok) setSplitFor(null);
          }}
        />
      )}

      {headOpen && (
        <MeetingDialog
          meeting={round}
          saving={busy}
          contactSuggestions={people.map((p) => ({ value: p.value, label: p.label }))}
          onCancel={() => setHeadOpen(false)}
          onSubmit={async (value) => {
            const ok = await run(
              () =>
                round
                  ? api.updateMeeting(round.id, value)
                  : api.createMeeting(value),
              t("error.title", "OPL nicht verfügbar"),
            );
            if (ok) setHeadOpen(false);
          }}
        />
      )}

      {absence && (
        <AbsenceWarning
          message={absence.message}
          busy={busy}
          onCancel={() => setAbsence(null)}
          onConfirm={() => absence.retry()}
        />
      )}
    </div>
  );
};

export default OpsanioOplPage;

export { OpsanioOplOverviewPage } from "./overview";
